import React from "react";
import { formatDate, formatDateTime } from "../../utils/commonHelper";
import { useNavigate } from "react-router-dom";
import {
  FaFileInvoice,
  FaTag,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
} from "react-icons/fa";
import { MdPayment } from "react-icons/md";
import GeneralButton from "../../common/GeneralButton";

const Details = ({ payment }) => {
  const navigate = useNavigate();

  const handleNavigation = () => {
    if (payment.subscriptionId)
      navigate(`/account/subscriptions/${payment.subscriptionId}`);
    else if (payment.orderId) navigate(`/account/orders/${payment.orderId}`);
  };
  const getStatusProps = (status) => {
    switch (status) {
      case "0":
        return { text: "Pending", bgColor: "#D26E32" };
      case "1":
        return { text: "Delivered", bgColor: "#A1B030" };
      case "2":
        return { text: "Cancelled", bgColor: "#888" };
      default:
        return { text: status || "Unknown", bgColor: "#888" };
    }
  };
  const getStatus = (status) => {
    switch (status) {
      case "1":
        return (
          <span className="flex items-center gap-1 text-green bg-green/10 px-3 py-1 rounded-full text-sm ">
            <FaCheckCircle className="me-1" /> Paid
          </span>
        );
      case "2":
        return (
          <span className="flex items-center gap-1 text-red-500 bg-red-100 px-3 py-1 rounded-full text-sm ">
            <FaTimesCircle className="me-1" /> Cancelled
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-orange bg-orange/10 px-3 py-1 rounded-full text-sm ">
            <FaClock className="me-1" /> Pending
          </span>
        );
    }
  };

  if (!payment) return null;
  const statusProps = getStatusProps(payment?.order?.orderStatus);
  return (
    <div className="p-6 lg:px-12 lg:py-10 bg-[#f7f9f6] rounded-[15px] shadow-sm space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray pb-3">
        <h3 className="text-xl flex items-center gap-2 text-gray-800">
          Payment Details
        </h3>

        {(payment.subscriptionId || payment.orderId) && (
          <GeneralButton
            text={`View ${
              payment.purpose === "Meal Subscription" ? "Subscription" : "Order"
            }`}
            onClick={handleNavigation}
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-gray-800">
        {/* LEFT SIDE — General Info */}
        <div className="space-y-3">
          {/* <div className="flex items-center gap-2">
            <span>Transaction ID:</span>
            <span className="ml-auto text-gray-600 font-mono text-16pt">
              {payment.stripePaymentId || "-"}
            </span>
          </div> */}

          <div className="flex items-center gap-2">
            <span>Payment Date:</span>
            <span className="ml-auto text-gray-600">
              {formatDate(payment.updatedAt)}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span>Purpose:</span>
            <span className="ml-auto text-gray-600">
              {payment.purpose || "-"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span>Status:</span>
            <div className="ml-auto">{getStatus(payment.status)}</div>
          </div>

          <div className="flex items-center gap-2">
            <span>Invoice:</span>
            <a
              href={payment.invoice ? `/${payment.invoice}` : "#"}
              target="_blank"
              rel="noopener noreferrer"
              className={`ml-auto ${
                payment.invoice
                  ? "text-green hover:underline"
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              {payment.invoice ? "Download" : "Not Available"}
            </a>
          </div>
        </div>

        {/* RIGHT SIDE — Amount Summary */}
        <div className="space-y-3 bg-white rounded-xl p-5 shadow-sm">
          {payment?.appliedCouponId && (
            <div className="flex items-center justify-between bg-orange/10 px-3 py-2 rounded-lg border border-orange text-sm">
              <span className="flex items-center gap-2 text-orange">
                <FaTag /> Coupon Applied
              </span>
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-mono text-16pt">
              {payment.subTotal ? `${payment.subTotal} AED` : "-"}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600">VAT:</span>
            <span>{payment.vatAmount ? `${payment.vatAmount} AED` : "-"}</span>
          </div>

          {payment.shippingCharges?.$numberDecimal > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Shipping:</span>
              <span>{payment.shippingCharges.$numberDecimal} AED</span>
            </div>
          )}
          {payment.depositAmount?.$numberDecimal > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Deposit (refundable):</span>
              <span>{payment.depositAmount.$numberDecimal} AED</span>
            </div>
          )}
          {payment?.depositAmount?.$numberDecimal > 0 &&
            (payment?.depositAmountFromPreviousSubscription === "1" ||
              payment?.depositAmountFromPreviousSubscription === 1) && (
              <div className="w-fit bg-orange/10  border-orange px-2 py-1 mt-0 rounded-md shadow-sm flex items-center gap-4">
                <p className="text-orange text-[13px] font-mono">
                 The deposit amount has been carried forward from the previous subscription upon renewal.
                </p>
              </div>
            )}
          <div className={"border-t border-gray pt-3 flex justify-between text-base"}>
            <span>Total Amount:</span>
            <span className="text-orange">
              {payment.totalAmount ? `${payment.totalAmount} AED` : "-"}
            </span>
          </div>

          <div className="flex justify-between text-lg">
            <span>Paid Amount:</span>
            <span className="text-green">
              {payment.paidAmount ? `${payment.paidAmount} AED` : "-"}
            </span>
          </div>
        </div>
      </div>

      {/* EXTRA SECTION — Subscription / Order Small Info */}
      {payment?.purpose === "Meal Subscription" && payment?.subscription && (
        <div className="bg-white rounded-xl p-5 shadow-sm mt-2 border border-gray">
          <h6 className="text-xl  mb-4 text-custom-3">Subscription Details</h6>
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
            <p className="text-16pt">
              <span className="font-mono text-16pt ">Plan:</span>{" "}
              {payment?.subscription?.plan?.title || "Unavailable"}
            </p>
            <p className="text-16pt">
              <span className="font-mono text-16pt ">Total Meals in Plan:</span>{" "}
              {payment?.subscription?.totalMealsInPlan}
            </p>
            <p className="text-16pt">
              <span className="font-mono text-16pt ">Type:</span>{" "}
              {payment?.subscription?.vegOrNonVeg}
            </p>
            <p className="text-16pt">
              <span className="font-mono text-16pt ">Start Date:</span>{" "}
              {formatDate(payment?.subscription?.subscriptionStartDate)}
            </p>
            <p className="text-16pt">
              <span className="font-mono text-16pt ">End Date:</span>{" "}
              {formatDate(payment?.subscription?.subscriptionEndDate)}
            </p>
            <p className="flex ">
              <span className="font-mono text-16pt ">Status:</span>{" "}
              {payment?.subscription?.status && (
                <GeneralButton
                  text={payment?.subscription?.status}
                  textColor="#fff"
                  icon={null}
                  bgColor={
                    payment?.subscription?.status === "Active"
                      ? "#A1B030"
                      : payment?.subscription?.status === "Cancelled"
                        ? "#D26E32"
                        : "#888"
                  }
                  className="cursor-default ms-1"
                />
              )}
            </p>
          </div>
        </div>
      )}

      {payment.purpose === "Product Order" && payment.order && (
        <div className="bg-white rounded-xl p-5 shadow-sm mt-2 border border-gray">
          <h6 className="text-xl  mb-4">Order Details</h6>
          <div className="grid sm:grid-cols-2 gap-3 text-gray-700 ">
            <p className="text-16pt">
              <span className="font-mono text-16pt">Order ID:</span>{" "}
              {payment.order.orderId}
            </p>

            <p className="text-16pt">
              <span className="font-mono text-16pt">Shipping:</span>{" "}
              {payment.order.shippingCharges?.$numberDecimal} AED
            </p>
            <p className="text-16pt">
              <span className="font-mono text-16pt">Products:</span>{" "}
              {payment.order.numberOfProducts}
            </p>
            <p className="text-16pt">
              <span className="font-mono text-16pt">VAT:</span>{" "}
              {payment.order.vatAmount?.$numberDecimal} AED
            </p>

            <p className="flex">
              <span className="font-mono text-16pt">Status:</span>{" "}
              <div className="flex items-center justify-start mt-1 lg:mt-0">
                <GeneralButton
                  text={statusProps.text}
                  textColor="#fff"
                  icon={null}
                  bgColor={statusProps.bgColor}
                  className="cursor-default ms-1 text-xs"
                />
              </div>
            </p>
            <p className="text-16pt">
              <span className="font-mono text-16pt">Total:</span>{" "}
              {payment.order.totalAmount?.$numberDecimal} AED
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Details;
